/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import ColumnDataset from './column';
import { toRaphaelColor, getFirstValue, regex, HASHSTRING, fcEach } from '../lib/lib';
import { getLightColor } from '../lib/lib-graphics';
import marimekkoAnimation from '../animation-rules/marimekko-animation';
import { addDep } from '../dependency-manager';
let dropHash = regex.dropHypeash,
  math = Math,
  mathRound = math.round,
  mathAbs = math.abs;
addDep({
  name: 'marimekkoAnimation',
  type: 'animationRule',
  extension: marimekkoAnimation
});
/**
 * class for Marimekko dataset
 */
class MarimekkoDataset extends ColumnDataset {
  /**
   * Constructor function of MarimekkoDataset
   */
  constructor () {
    super();
    this.config.groupName = 'column';
  }
  /**
   * Sets the type of the component
   * @return {string} type
   */
  getType () {
    return 'dataset';
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'marimekko';
  }
  /**
   * Function to configure the dataset
   * @param  {Object} datasetJSON JSON for dataset configurations
   */
  configure (datasetJSON) {
    fcEach(datasetJSON.data, function (obj) {
      if (obj) {
        obj.value = mathAbs(obj.value);
      }
    });

    super.configure(datasetJSON);
  }

  /**
   * Function to register datasets in the internal data store of legend class.
   */
  _addLegend () {
    var dataset = this,
      strokeColor,
      fillColor,
      config,
      legendItem,
      conf = dataset.config,
      legend = dataset.getFromEnv('legend'),
      color = conf.legendSymbolColor;
    strokeColor = getLightColor(color, 60).replace(dropHash, HASHSTRING);
    fillColor = {
      FCcolor: {
        color: color,
        angle: 0,
        ratio: '0',
        alpha: '100'
      }
    };
    config = {
      enabled: conf.includeInLegend,
      type: dataset.type,
      label: getFirstValue(dataset.config.JSONData.seriesname)
    };
    if (conf.includeinlegend) {
      legendItem = legend.getItem(dataset.config.legendItemId);
      if (!legendItem) {
        dataset.config.legendItemId = legend.createItem(dataset);
        legendItem = legend.getItem(dataset.config.legendItemId);
        dataset.addExtEventListener('click', function () {
          legendItem.itemClickFn();
        }, legendItem);
      }
      legendItem.configure(config);
      legendItem.setStateCosmetics('default', {
        symbol: {
          fill: toRaphaelColor(fillColor),
          rawFillColor: fillColor.FCcolor.color,
          stroke: toRaphaelColor(strokeColor)
        }
      });
      // check if dataset is initiallyhidden
      if (!dataset.getState('visible')) {
        legendItem.setLegendState('hidden');
      } else { // remove hidden state of legend item is dataset is not hidden
        legendItem.removeLegendState('hidden');
      }
    } else if (dataset.config.legendItemId) {
      legend.disposeItem(dataset.config.legendItemId);
    }
  }

  /**
   * Function returns the nearest dataset index
   * @param {number} searchElementIndex The derived search index as per the mouse postion
   * @param {Array} arr Has the information about each stack
   * @return {number} The found dataset index
   */
  searchIndex (searchElementIndex, arr) {
    var dataset = this,
      // chart = dataset.chart,
      xAxis = dataset.getFromEnv('xAxis'),
      minIndex = 0,
      len = arr.length - 1,
      maxIndex = len,
      currentIndex,
      currentElementIndex;

    while (minIndex <= maxIndex) {
      currentIndex = Math.round((minIndex + maxIndex) / 2) || 0;
      currentElementIndex = xAxis.getPixel(arr[currentIndex].x) +
                 arr[currentIndex].columnWidth / 2;

      if (currentElementIndex < searchElementIndex) {
        minIndex = currentIndex + 1;
      } else if (currentElementIndex > searchElementIndex) {
        maxIndex = currentIndex - 1;
      } else {
        return currentIndex;
      }
    }
    return minIndex;
  }

  /**
   * Allocates positions and parses attributes of plot during post spacemanagement
   *
   * @memberof MarimekkoDataset
   */
  allocatePosition () {
    this.getLinkedParent()._setStackDimensions();
    super.allocatePosition();
  }
  /**
   * Function that return the nearest plot details
   * @param {number} chartX x-axis position of the mouse cordinate
   * @param {number} chartY x-axis position of the mouse cordinate
   * @return {Object} return an object with details of nearest polt and whether it is hovered or not
  */
  _getHoveredPlot (chartX, chartY) {
    var dataset = this,
      groupManager = dataset.getLinkedParent(),
      stackConf = groupManager.getstackConf(),
      // chart = dataset.chart,
      chartConfig = dataset.getFromEnv('chartConfig'),
      configManager = groupManager.config,
      plotBorderThickness = chartConfig.plotborderthickness,
      showPlotBorder = chartConfig.showplotborder,
      len = (stackConf.length - 1),
      halfPlotBorderThickness,
      xPos,
      returnValue,
      datasetIndex;

    plotBorderThickness = showPlotBorder ? plotBorderThickness : 0;
    halfPlotBorderThickness = plotBorderThickness / 2;
    halfPlotBorderThickness = halfPlotBorderThickness % 2 === 0 ?
      halfPlotBorderThickness + 1 : Math.round(halfPlotBorderThickness);
    xPos = chartX + halfPlotBorderThickness;
    datasetIndex = (returnValue && configManager.datasetIndex) || dataset.searchIndex(xPos, stackConf);
    // save dataIndex to prevent unnecessary search
    configManager.datasetIndex || (configManager.datasetIndex = datasetIndex);
    // Checking for overlap between two cosecutive column plots along x-axis
    returnValue = dataset._checkPointerOverColumn(datasetIndex, chartX, chartY);
    // delete dataIndex
    returnValue ? delete configManager.datasetIndex :
      (dataset.index === len && (delete configManager.datasetIndex));

    return returnValue;
  }

  /**
   * Over-writting the function
   * @return {Object} returning the this object to avoid lint error
   */
  setColumnPosition () {
    return this;
  }

  /**
   * Setting the width, xPos of the individual column data such that the corresponding stacks share a common boundary
   * @param {number} xPos The pre-calculated x position
   * @param {number} yPos The pre-calculated y position
   * @param {number} height The pre-calculated height position
   * @param {number} width The pre-calculated width position
   * @param {number} index The index of the data
   * @return {Object} the calculated xPos, yPos, width and height
   */
  fineTunePlotDimension (xPos, yPos, height, width, index) {
    var dataSet = this,
      parent = dataSet.getLinkedParent(),
      stackConf = parent.getstackConf(),
      chart = dataSet.getFromEnv('chart'),
      chartConfig = chart.config,
      plotBorderThickness = chartConfig.plotborderthickness,
      canvasConfig = chart.getChildren('canvas')[0].config,
      canvasBorderWidth = canvasConfig.canvasBorderWidth,
      hasValidCanvasBorder = canvasBorderWidth > 0,
      canvasRight = canvasConfig.canvasRight,
      canvasTop = canvasConfig.canvasTop,
      canvasLeft = canvasConfig.canvasLeft;

    // if there is no stackConf then return the pre-calculated position
    if (!stackConf.length) {
      return {
        xPos: xPos,
        yPos: yPos,
        width: width,
        height: height
      };
    }
    width = stackConf[index].columnWidth;
    xPos -= width / 2;
    if (parseInt(yPos) <= canvasTop) {
      height -= canvasTop - yPos -
                    (+hasValidCanvasBorder);
      yPos = canvasTop - (+hasValidCanvasBorder);
    }

    if (plotBorderThickness <= 1) {
      // in case xPos is right of canvas-left position -
      // normalize
      if (mathRound(xPos) <= canvasLeft) {
        width += xPos;
        xPos = canvasLeft - (plotBorderThickness / 2) +
                        (+!!plotBorderThickness) - (+hasValidCanvasBorder);
        width -= xPos;
      }
      // in case plot exceeds canvas-right position -
      // normalize width
      if (mathRound(xPos + width) >= canvasRight) {
        width = canvasRight - xPos + (plotBorderThickness / 2) -
                        (+!!plotBorderThickness) + (+hasValidCanvasBorder);
      }
    }

    return {
      xPos: xPos,
      yPos: yPos,
      width: width,
      height: height
    };
  }
}
export default MarimekkoDataset;
